import React, { useEffect, useMemo, useState } from 'react';
import EventSystemTabLayout from '../../components/layout/EventSystemTabLayout';
import { useTenant } from '@libs/contexts/TenantContext';
import { collection, onSnapshot, query } from 'firebase/firestore';
import { db } from '../../services/firebase';
import { createCallRoutingRule, deleteCallRoutingRule, searchTenantUsersAdmin, updateCallRoutingRule } from '../../api/backendApi';
import { getFlowsStable } from '../../services/eventSystemApiServiceStable';
import {
  Box,
  Button,
  CircularProgress,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Alert,
} from '@mui/material';
import {
  ROUTE_TYPE_FLOW,
  ROUTE_TYPE_USER,
  canSaveCallRouting,
  defaultCallRoutingForm,
  getUserLabel,
  inboundRoutingFlowLabel,
  isFlowRoute,
  isInboundRoutingFlow,
  routeToDisplay,
  toClientIdentity,
} from './callRoutingModel';

function CallRoutingModal({
  open,
  formData,
  onClose,
  onChange,
  onSave,
  isEditing,
  routeToSearchTerm,
  onRouteToSearchChange,
  userSearchResults,
  userSearchLoading,
  onSelectRouteUser,
  flowOptions,
  flowOptionsLoading,
  onSelectRouteFlow,
  saveError,
  saving,
}) {
  const flowMode = isFlowRoute(formData.routeType);
  const filteredFlows = useMemo(() => {
    const term = (routeToSearchTerm || '').trim().toLowerCase();
    const list = (flowOptions || []).filter(isInboundRoutingFlow);
    if (!term) return list;
    return list.filter((flow) => {
      const label = inboundRoutingFlowLabel(flow).toLowerCase();
      const id = String(flow.id || '').toLowerCase();
      return label.includes(term) || id.includes(term);
    });
  }, [flowOptions, routeToSearchTerm]);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{isEditing ? 'Edit Call Routing Rule' : 'Add Call Routing Rule'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            label="Label"
            value={formData.label}
            onChange={(e) => onChange('label', e.target.value)}
            placeholder="Front Door"
            fullWidth
            required
            inputProps={{ 'aria-label': 'Rule label' }}
          />

          <FormControl fullWidth>
            <InputLabel id="call-routing-match-type">Match Type</InputLabel>
            <Select
              labelId="call-routing-match-type"
              label="Match Type"
              value={formData.matchType}
              onChange={(e) => onChange('matchType', e.target.value)}
            >
              <MenuItem value="extension">Extension</MenuItem>
              <MenuItem value="sip_username">SIP Username</MenuItem>
              <MenuItem value="caller_name">Caller Name</MenuItem>
            </Select>
          </FormControl>

          <TextField
            label="Match Value"
            value={formData.matchValue}
            onChange={(e) => onChange('matchValue', e.target.value)}
            placeholder="101 or doorphone01"
            fullWidth
            required
            inputProps={{ 'aria-label': 'Match value' }}
          />

          <FormControl fullWidth>
            <InputLabel id="call-routing-device-type">Device Type</InputLabel>
            <Select
              labelId="call-routing-device-type"
              label="Device Type"
              value={formData.deviceType}
              onChange={(e) => onChange('deviceType', e.target.value)}
            >
              <MenuItem value="doorphone">Doorphone</MenuItem>
              <MenuItem value="sip_phone">SIP Phone</MenuItem>
              <MenuItem value="other">Other</MenuItem>
            </Select>
          </FormControl>

          <FormControl fullWidth>
            <InputLabel id="call-routing-route-type">Route Type</InputLabel>
            <Select
              labelId="call-routing-route-type"
              label="Route Type"
              value={formData.routeType || ROUTE_TYPE_USER}
              onChange={(e) => onChange('routeType', e.target.value)}
              inputProps={{ 'aria-label': 'Route Type' }}
            >
              <MenuItem value={ROUTE_TYPE_USER}>User</MenuItem>
              <MenuItem value={ROUTE_TYPE_FLOW}>Flow</MenuItem>
            </Select>
          </FormControl>

          {flowMode ? (
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <TextField
                  label="Route To"
                  value={routeToSearchTerm}
                  onChange={(e) => onRouteToSearchChange(e.target.value)}
                  placeholder="Search inbound routing Flows"
                  helperText={
                    formData.routeTo
                      ? `Selected Flow: ${formData.routeToLabel || formData.routeTo}`
                      : 'Only Flows with a Route Call step can handle inbound SIP routing'
                  }
                  fullWidth
                  required
                  inputProps={{ 'aria-label': 'Route To Flow' }}
                />
                {flowOptionsLoading && <CircularProgress size={20} />}
              </Box>
              {filteredFlows.length > 0 && (
                <Box
                  data-testid="call-routing-flow-results"
                  sx={{
                    maxHeight: 180,
                    overflowY: 'auto',
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    mt: 1,
                    p: 1,
                  }}
                >
                  {filteredFlows.map((flow) => {
                    const id = flow?.id;
                    if (!id) return null;
                    const label = inboundRoutingFlowLabel(flow);
                    const selected = formData.routeTo === id;
                    return (
                      <Box
                        key={id}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          py: 0.5,
                          gap: 1,
                        }}
                      >
                        <Box sx={{ flex: 1 }}>
                          <div style={{ fontSize: 14 }}>{label || id}</div>
                          <div style={{ fontSize: 11, color: '#6B7280' }}>{id}</div>
                        </Box>
                        <Button
                          size="small"
                          variant={selected ? 'outlined' : 'contained'}
                          onClick={() => onSelectRouteFlow(flow)}
                        >
                          {selected ? 'Selected' : 'Select'}
                        </Button>
                      </Box>
                    );
                  })}
                </Box>
              )}
            </Box>
          ) : (
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <TextField
                  label="Route To"
                  value={routeToSearchTerm}
                  onChange={(e) => onRouteToSearchChange(e.target.value)}
                  placeholder="Type at least 2 characters to search users"
                  helperText={formData.routeTo ? `Selected identity: ${formData.routeTo}` : 'Type at least 2 characters to search users'}
                  fullWidth
                  required
                  inputProps={{ 'aria-label': 'Route To User' }}
                />
                {userSearchLoading && <CircularProgress size={20} />}
              </Box>

              {userSearchResults.length > 0 && (
                <Box
                  data-testid="call-routing-user-results"
                  sx={{
                    maxHeight: 180,
                    overflowY: 'auto',
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    mt: 1,
                    p: 1,
                  }}
                >
                  {userSearchResults.map((user) => {
                    const uid = user?.id;
                    if (!uid) return null;
                    const label = getUserLabel(user);
                    const identity = toClientIdentity(uid);
                    const selected = formData.routeTo === identity;

                    return (
                      <Box
                        key={uid}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          py: 0.5,
                          gap: 1,
                        }}
                      >
                        <Box sx={{ flex: 1 }}>
                          <div style={{ fontSize: 14 }}>{label || uid}</div>
                          {user.email && label !== user.email && (
                            <div style={{ fontSize: 11, color: '#6B7280' }}>{user.email}</div>
                          )}
                        </Box>
                        <Button
                          size="small"
                          variant={selected ? 'outlined' : 'contained'}
                          onClick={() => onSelectRouteUser(user)}
                        >
                          {selected ? 'Selected' : 'Select'}
                        </Button>
                      </Box>
                    );
                  })}
                </Box>
              )}
            </Box>
          )}

          <FormControl fullWidth>
            <InputLabel id="call-routing-status">Status</InputLabel>
            <Select
              labelId="call-routing-status"
              label="Status"
              value={formData.status}
              onChange={(e) => onChange('status', e.target.value)}
            >
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="disabled">Disabled</MenuItem>
            </Select>
          </FormControl>
          {saveError ? <Alert severity="error">{saveError}</Alert> : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={onSave}
          disabled={!canSaveCallRouting(formData) || Boolean(saving)}
        >
          {isEditing ? 'Save Changes' : 'Add Route'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

const CallRoutingPage = () => {
  const { selectedTenant } = useTenant();
  const tenantId = selectedTenant?.id || '';
  const [routes, setRoutes] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [formData, setFormData] = useState(defaultCallRoutingForm);
  const [editingId, setEditingId] = useState('');
  const [routeToSearchTerm, setRouteToSearchTerm] = useState('');
  const [userSearchResults, setUserSearchResults] = useState([]);
  const [userSearchLoading, setUserSearchLoading] = useState(false);
  const [flowOptions, setFlowOptions] = useState([]);
  const [flowOptionsLoading, setFlowOptionsLoading] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [saving, setSaving] = useState(false);

  const isEditing = useMemo(() => Boolean(editingId), [editingId]);

  useEffect(() => {
    if (!tenantId) {
      setRoutes([]);
      return;
    }

    const routesRef = collection(db, 'tenants', String(tenantId), 'callRouting');
    const routesQuery = query(routesRef);

    const unsubscribe = onSnapshot(
      routesQuery,
      (snapshot) => {
        const next = snapshot.docs.map((routeDoc) => {
          const data = routeDoc.data() || {};
          return {
            id: routeDoc.id,
            label: data.label || '',
            matchType: data.matchType || 'extension',
            matchValue: data.matchValue || '',
            deviceType: data.deviceType || 'doorphone',
            routeType: data.routeType || ROUTE_TYPE_USER,
            routeTo: data.routeTo || '',
            routeToLabel: data.routeToLabel || '',
            status: data.status || 'active',
          };
        });
        setRoutes(next);
      },
      (error) => {
        console.error('[CallRouting] Failed to load routes:', error);
      }
    );

    return () => unsubscribe();
  }, [tenantId]);

  const handleOpenCreate = () => {
    setEditingId('');
    setFormData(defaultCallRoutingForm);
    setRouteToSearchTerm('');
    setUserSearchResults([]);
    setFlowOptions([]);
    setModalOpen(true);
  };

  const handleOpenEdit = (route) => {
    setEditingId(route.id);
    setFormData({ ...defaultCallRoutingForm, ...route });
    setRouteToSearchTerm(route.routeToLabel || route.routeTo || '');
    setUserSearchResults([]);
    setFlowOptions([]);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setEditingId('');
    setFormData(defaultCallRoutingForm);
    setRouteToSearchTerm('');
    setUserSearchResults([]);
    setUserSearchLoading(false);
    setFlowOptions([]);
    setFlowOptionsLoading(false);
    setSaveError('');
    setSaving(false);
  };

  const handleFormChange = (field, value) => {
    setFormData((prev) => {
      if (field === 'routeType' && value !== prev.routeType) {
        return { ...prev, routeType: value, routeTo: '', routeToLabel: '' };
      }
      return { ...prev, [field]: value };
    });
    if (field === 'routeType') {
      setRouteToSearchTerm('');
      setUserSearchResults([]);
    }
  };

  const handleRouteToSearchChange = (value) => {
    setRouteToSearchTerm(value);
    if (!value.trim()) {
      setFormData((prev) => ({ ...prev, routeTo: '', routeToLabel: '' }));
    }
  };

  const handleSelectRouteUser = (user) => {
    const uid = user?.id;
    if (!uid) return;

    const label = getUserLabel(user);
    const identity = toClientIdentity(uid);
    setFormData((prev) => ({
      ...prev,
      routeType: ROUTE_TYPE_USER,
      routeTo: identity,
      routeToLabel: label,
    }));
    setRouteToSearchTerm(label || identity);
  };

  const handleSelectRouteFlow = (flow) => {
    const id = flow?.id;
    if (!id) return;
    const label = inboundRoutingFlowLabel(flow);
    setFormData((prev) => ({
      ...prev,
      routeType: ROUTE_TYPE_FLOW,
      routeTo: id,
      routeToLabel: label,
    }));
    setRouteToSearchTerm(label || id);
  };

  useEffect(() => {
    let active = true;
    const runSearch = async () => {
      const term = (routeToSearchTerm || '').trim();
      if (!modalOpen || !tenantId || isFlowRoute(formData.routeType) || term.length < 2) {
        if (active) {
          setUserSearchResults([]);
          setUserSearchLoading(false);
        }
        return;
      }

      setUserSearchLoading(true);
      try {
        const results = await searchTenantUsersAdmin(tenantId, term, 25);
        if (!active) return;
        setUserSearchResults(Array.isArray(results) ? results : []);
      } catch (error) {
        if (!active) return;
        setUserSearchResults([]);
      } finally {
        if (active) setUserSearchLoading(false);
      }
    };

    const timer = setTimeout(runSearch, 300);
    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [modalOpen, tenantId, routeToSearchTerm, formData.routeType]);

  useEffect(() => {
    let active = true;
    if (!modalOpen || !tenantId || !isFlowRoute(formData.routeType)) {
      return undefined;
    }
    setFlowOptionsLoading(true);
    getFlowsStable(tenantId, { capability: 'inbound_call_routing' })
      .then((list) => {
        if (!active) return;
        setFlowOptions(Array.isArray(list) ? list : []);
      })
      .catch(() => {
        if (!active) return;
        setFlowOptions([]);
      })
      .finally(() => {
        if (active) setFlowOptionsLoading(false);
      });
    return () => {
      active = false;
    };
  }, [modalOpen, tenantId, formData.routeType]);

  const handleSave = async () => {
    if (!tenantId || !canSaveCallRouting(formData) || saving) {
      return;
    }

    const payload = {
      label: formData.label,
      matchType: formData.matchType,
      matchValue: formData.matchValue,
      deviceType: formData.deviceType,
      routeType: formData.routeType || ROUTE_TYPE_USER,
      routeTo: formData.routeTo,
      routeToLabel: formData.routeToLabel,
      status: formData.status,
    };

    setSaving(true);
    setSaveError('');
    try {
      if (isEditing) {
        await updateCallRoutingRule(tenantId, editingId, payload);
      } else {
        await createCallRoutingRule(tenantId, payload);
      }
      handleCloseModal();
    } catch (error) {
      console.error('[CallRouting] Failed to save route:', error);
      const raw = String(error?.message || 'Failed to save Call Routing rule');
      const stripped = raw.replace(/^API Error \(\d+\):\s*/, '');
      let display = stripped;
      try {
        const parsed = JSON.parse(stripped);
        display = parsed.error || parsed.message || stripped;
      } catch {
        display = stripped;
      }
      setSaveError(display);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!tenantId || !id) {
      return;
    }

    try {
      await deleteCallRoutingRule(tenantId, id);
    } catch (error) {
      console.error('[CallRouting] Failed to delete route:', error);
    }
  };

  const getMatchLabel = (route) => {
    const map = {
      extension: 'Extension',
      sip_username: 'SIP Username',
      caller_name: 'Caller Name',
    };
    return `${map[route.matchType] || route.matchType}: ${route.matchValue}`;
  };

  const getDeviceTypeLabel = (value) => {
    if (value === 'doorphone') return 'Doorphone';
    if (value === 'sip_phone') return 'SIP Phone';
    return 'Other';
  };

  if (!selectedTenant) {
    return (
      <EventSystemTabLayout pageTitle="Call Routing" showPageTitle={false}>
        <Box sx={{ p: 3, textAlign: 'center' }}>
          <Typography color="text.secondary">Please select a tenant to configure call routing.</Typography>
        </Box>
      </EventSystemTabLayout>
    );
  }

  return (
    <EventSystemTabLayout pageTitle="Call Routing" showPageTitle={false}>
      <Box sx={{ p: 2 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2, alignItems: { sm: 'center' }, justifyContent: 'space-between' }}>
          <Stack spacing={0.5}>
            <Typography variant="h5" sx={{ fontWeight: 700 }}>Call Routing</Typography>
            <Typography variant="body2" color="text.secondary">
              Inbound SIP match → User or Flow. A Flow Route Call connects this same inbound call; it does not place a new call.
            </Typography>
          </Stack>
          <Button variant="contained" onClick={handleOpenCreate}>Add Route</Button>
        </Stack>

        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700 }}>Label</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Match</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Device Type</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Route To</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {routes.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">No call routing rules yet.</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                routes.map((route) => (
                  <TableRow key={route.id} hover>
                    <TableCell>{route.label}</TableCell>
                    <TableCell>{getMatchLabel(route)}</TableCell>
                    <TableCell>{getDeviceTypeLabel(route.deviceType)}</TableCell>
                    <TableCell>{routeToDisplay(route)}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={route.status === 'active' ? 'Active' : 'Disabled'}
                        color={route.status === 'active' ? 'success' : 'default'}
                        variant={route.status === 'active' ? 'filled' : 'outlined'}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Stack direction="row" spacing={1} sx={{ justifyContent: 'flex-end' }}>
                        <Button size="small" variant="outlined" onClick={() => handleOpenEdit(route)}>Edit</Button>
                        <Button size="small" color="error" variant="outlined" onClick={() => handleDelete(route.id)}>Delete</Button>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      <CallRoutingModal
        open={modalOpen}
        formData={formData}
        onClose={handleCloseModal}
        onChange={handleFormChange}
        onSave={handleSave}
        isEditing={isEditing}
        routeToSearchTerm={routeToSearchTerm}
        onRouteToSearchChange={handleRouteToSearchChange}
        userSearchResults={userSearchResults}
        userSearchLoading={userSearchLoading}
        onSelectRouteUser={handleSelectRouteUser}
        flowOptions={flowOptions}
        flowOptionsLoading={flowOptionsLoading}
        onSelectRouteFlow={handleSelectRouteFlow}
        saveError={saveError}
        saving={saving}
      />
    </EventSystemTabLayout>
  );
};

export default CallRoutingPage;
